import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  LocationOn,
  ExitToApp,
  Login,
  CameraAltOutlined,
  ArrowForward,
} from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import RatingDialog from '../../components/RatingDialog';
import GlassCard from '../../components/shared/GlassCard';
import EmptyState from '../../components/shared/EmptyState';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { RADIUS, GRADIENTS } from '../../theme/tokens';

const MotionBox = motion(Box);

// Scanner status values
const STATUS = {
  SCANNING: 'scanning',
  FOUND: 'found',
  ERROR: 'error',
  SUCCESS: 'success',
  PERMISSION_DENIED: 'permission_denied',
};

// Corner bracket SVG for QR framing overlay
function CornerBrackets({ size = 240, color = 'rgba(242,101,34,0.9)' }) {
  const len = 32;
  const stroke = 4;
  const r = 4;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ position: 'absolute', inset: 0, margin: 'auto', pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {/* Top-left */}
      <path d={`M ${stroke} ${stroke + len} L ${stroke} ${stroke + r} Q ${stroke} ${stroke} ${stroke + r} ${stroke} L ${stroke + len} ${stroke}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      {/* Top-right */}
      <path d={`M ${size - stroke - len} ${stroke} L ${size - stroke - r} ${stroke} Q ${size - stroke} ${stroke} ${size - stroke} ${stroke + r} L ${size - stroke} ${stroke + len}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      {/* Bottom-right */}
      <path d={`M ${size - stroke} ${size - stroke - len} L ${size - stroke} ${size - stroke - r} Q ${size - stroke} ${size - stroke} ${size - stroke - r} ${size - stroke} L ${size - stroke - len} ${size - stroke}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      {/* Bottom-left */}
      <path d={`M ${stroke + len} ${size - stroke} L ${stroke + r} ${size - stroke} Q ${stroke} ${size - stroke} ${stroke} ${size - stroke - r} L ${stroke} ${size - stroke - len}`} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" />
    </svg>
  );
}

// Animated horizontal scan-line sweeping top-to-bottom
function ScanLine({ color = 'rgba(242,101,34,0.6)', reducedMotion }) {
  if (reducedMotion) return null;
  return (
    <motion.div
      aria-hidden="true"
      animate={{ y: ['0%', '100%', '0%'] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 2,
        background: color,
        pointerEvents: 'none',
      }}
    />
  );
}

/**
 * StudentScanner — full-viewport QR check-in/check-out scanner.
 * No bottom nav or ActiveSessionBanner — the shell hides chrome for this route.
 * All API calls and payloads are identical to the original.
 */
function StudentScanner() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const reducedMotion = useReducedMotionPreferred();

  const [status, setStatus] = useState(STATUS.SCANNING);
  const [scannerError, setScannerError] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [ratingDialog, setRatingDialog] = useState({ open: false, bookingId: null, libraryName: '' });
  const [processing, setProcessing] = useState(false);

  const scannerInstanceRef = useRef(null);
  const activeSessionRef = useRef(null);

  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await api.get('/sessions/active');
      setActiveSession(response.data.data.session ?? null);
    } catch {
      setActiveSession(null);
    }
  }, []);

  useEffect(() => {
    fetchActiveSession();

    const handleVisibility = () => {
      if (!document.hidden) fetchActiveSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const qrScanner = new Html5QrcodeScanner(
      'student-qr-reader',
      {
        fps: 10,
        qrbox: { width: 260, height: 260 },
        aspectRatio: 1.0,
        disableFlip: false,
        videoConstraints: {
          facingMode: { exact: 'environment' },
          advanced: [{ zoom: 2.0 }],
        },
        formatsToSupport: [0, 1],
      },
      false,
    );

    const handleScanSuccess = async (decodedText) => {
      setProcessing(true);
      setScannerError('');
      try {
        const payload = JSON.parse(decodedText);
        if (payload.type !== 'library') throw new Error('Invalid QR code. Please scan a library QR code.');

        const libraryResponse = await api.get(`/libraries/${payload.id}`);
        const library = libraryResponse.data.data.library;

        const currentSession = activeSessionRef.current;
        const action = currentSession ? 'check-out' : 'check-in';

        setStatus(STATUS.FOUND);
        setConfirmDialog({
          open: true,
          data: { libraryId: payload.id, libraryName: library.name, action },
        });
        qrScanner.pause();
      } catch (err) {
        // Flash error chip then resume
        setStatus(STATUS.ERROR);
        setScannerError(err.message || 'Invalid QR code');
        setTimeout(() => {
          setStatus(STATUS.SCANNING);
          setScannerError('');
        }, 1000);
      } finally {
        setProcessing(false);
      }
    };

    // Initialise; on permission denied the scanner will show its own error UI
    // but we also catch it below via getUserMedia
    qrScanner.render(handleScanSuccess, () => {
      // Frequent scan errors from frames — ignore silently
    });

    scannerInstanceRef.current = qrScanner;

    // Detect camera permission denial by probing early
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
      .then((stream) => stream.getTracks().forEach((t) => t.stop()))
      .catch(() => setStatus(STATUS.PERMISSION_DENIED));

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      qrScanner.clear().catch(() => {});
    };
  }, [fetchActiveSession]);

  const handleConfirmAction = async () => {
    const { libraryId, action } = confirmDialog.data;
    setScannerError('');

    try {
      if (action === 'check-in') {
        const response = await api.post('/sessions/start', {
          student_id: user.id,
          library_id: libraryId,
        });
        setScanResult({ action: 'check-in', session: response.data.data.session, library: response.data.data.library });
        setActiveSession(response.data.data.session);
      } else {
        const response = await api.post('/sessions/end', {
          session_id: activeSessionRef.current.id,
          library_id: libraryId,
        });
        const endedSession = response.data.data.session;
        const endedLibrary = response.data.data.library;
        setScanResult({ action: 'check-out', session: endedSession, library: endedLibrary });
        setActiveSession(null);

        // Show rating dialog after checkout (same timing as original)
        if (endedSession.booking_id) {
          setTimeout(() => {
            setRatingDialog({
              open: true,
              bookingId: endedSession.booking_id,
              libraryName: endedLibrary?.name ?? confirmDialog.data?.libraryName ?? 'Library',
            });
          }, 1500);
        }
      }
      setStatus(STATUS.SUCCESS);
      setConfirmDialog({ open: false, data: null });
    } catch (err) {
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.message || 'Action failed';

      if (errorCode === 'DAILY_LIMIT_REACHED') {
        setScannerError(`Daily limit reached! You've used all ${err.response?.data?.hoursPerDay} hours for today.`);
      } else if (errorCode === 'PAYMENT_PENDING') {
        setScannerError('Payment pending. Please pay at the library counter before checking in.');
      } else {
        setScannerError(errorMessage);
      }

      setStatus(STATUS.SCANNING);
      setConfirmDialog({ open: false, data: null });
      scannerInstanceRef.current?.resume();
    }
  };

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, data: null });
    setStatus(STATUS.SCANNING);
    scannerInstanceRef.current?.resume();
  };

  const handleReset = () => {
    setScanResult(null);
    setScannerError('');
    setStatus(STATUS.SCANNING);
    scannerInstanceRef.current?.resume();
  };

  const handleRequestPermission = () => {
    navigator.mediaDevices
      ?.getUserMedia({ video: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        setStatus(STATUS.SCANNING);
        // Reload so html5-qrcode can re-initialise with permission granted
        window.location.reload();
      })
      .catch(() => {
        setScannerError('Camera permission denied. Please enable it in browser settings.');
      });
  };

  // Status chip label / color
  const chipConfig = {
    [STATUS.SCANNING]: { label: 'Scanning…', color: 'default' },
    [STATUS.FOUND]:    { label: 'Found!',          color: 'primary' },
    [STATUS.ERROR]:    { label: 'Not recognised',  color: 'error' },
    [STATUS.SUCCESS]:  { label: 'Done',            color: 'success' },
  };
  const chip = chipConfig[status] ?? chipConfig[STATUS.SCANNING];

  // Full-screen success overlay
  if (status === STATUS.SUCCESS && scanResult) {
    const isCheckIn = scanResult.action === 'check-in';
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          pb: 6,
        }}
      >
        <MotionBox
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 240, damping: 20 }}
          sx={{ textAlign: 'center', maxWidth: 360, width: '100%' }}
        >
          {/* Brand-gradient circle with icon */}
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: isCheckIn ? GRADIENTS.brand : GRADIENTS.brandDeep,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            {isCheckIn
              ? <Login sx={{ color: 'white', fontSize: 48 }} />
              : <ExitToApp sx={{ color: 'white', fontSize: 48 }} />}
          </Box>

          <Typography variant="h4" fontWeight={800} color="text.primary" mb={0.5}>
            {isCheckIn ? 'Checked In!' : 'Checked Out!'}
          </Typography>

          {scanResult.library && (
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={3}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body1" color="text.secondary">
                {scanResult.library.name}
              </Typography>
            </Box>
          )}

          {scanResult.session && scanResult.session.entered_at && (
            <GlassCard sx={{ p: 2, mb: 3, textAlign: 'left' }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">Checked in</Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {new Date(scanResult.session.entered_at).toLocaleString('en-IN', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </Typography>
              </Box>
              {scanResult.session.exited_at && (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">Checked out</Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {new Date(scanResult.session.exited_at).toLocaleString('en-IN', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                  {scanResult.session.duration_seconds && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {Math.floor(scanResult.session.duration_seconds / 60)} min
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </GlassCard>
          )}

          {/* CTA row */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleReset}
              sx={{ borderRadius: RADIUS.pill, py: 1.5, textTransform: 'none', fontWeight: 700 }}
            >
              Done
            </Button>
            {isCheckIn && (
              <Button
                fullWidth
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/student/focus-mode')}
                sx={{ borderRadius: RADIUS.pill, py: 1.5, textTransform: 'none', fontWeight: 600 }}
              >
                Enter Focus Mode
              </Button>
            )}
          </Box>
        </MotionBox>

        {/* Rating dialog after checkout */}
        <RatingDialog
          open={ratingDialog.open}
          onClose={() => setRatingDialog({ open: false, bookingId: null, libraryName: '' })}
          bookingId={ratingDialog.bookingId}
          libraryName={ratingDialog.libraryName}
          onSuccess={() => {}}
        />
      </Box>
    );
  }

  // Camera permission denied state
  if (status === STATUS.PERMISSION_DENIED) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        <EmptyState
          icon={<CameraAltOutlined sx={{ fontSize: 48, color: 'rgba(255,255,255,0.5)' }} />}
          title="Camera access required"
          description="Enable camera access in your browser settings to scan QR codes."
          actionLabel="Enable Camera"
          onAction={handleRequestPermission}
          sx={{ color: 'white', '& .MuiTypography-root': { color: 'rgba(255,255,255,0.85)' } }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000', position: 'relative', overflow: 'hidden' }}>
      {/* Scanner region */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* html5-qrcode mounts here; styles strip its default chrome */}
        <Box
          id="student-qr-reader"
          sx={{
            width: '100%',
            maxWidth: 400,
            '& > div': { border: 'none !important' },
            '& video': { width: '100% !important', height: '100% !important', objectFit: 'cover' },
            '& #student-qr-reader__dashboard': { display: 'none !important' },
            '& #student-qr-reader__header_message': { display: 'none !important' },
          }}
        />
      </Box>

      {/* Framing overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ position: 'relative', width: 260, height: 260 }}>
          {/* Dim surrounding */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              zIndex: -1,
            }}
          />
          <CornerBrackets size={260} />
          <ScanLine reducedMotion={reducedMotion} />
        </Box>
      </Box>

      {/* Error banner */}
      <AnimatePresence>
        {scannerError && (
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20 }}
          >
            <Alert severity="error" sx={{ borderRadius: 0 }}>
              {scannerError}
            </Alert>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Bottom panel */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <GlassCard
          tint="dark"
          sx={{
            borderTopLeftRadius: RADIUS.xl,
            borderTopRightRadius: RADIUS.xl,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            p: 3,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            {/* Status chip */}
            <Chip
              label={chip.label}
              color={chip.color}
              size="small"
              sx={{ fontWeight: 600 }}
            />

            <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Point camera at library QR code
            </Typography>

            {/* Manual entry fallback */}
            <Button
              component="a"
              href="/student/scanner"
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.55)',
                textTransform: 'none',
                fontSize: 12,
                '&:hover': { color: 'rgba(255,255,255,0.85)' },
              }}
              onClick={(e) => {
                e.preventDefault();
                const code = window.prompt('Enter QR code value manually');
                if (code) {
                  // Trigger as if scanned — the scanner renders this as a text input fallback
                  const el = document.querySelector('#student-qr-reader__status_span');
                  if (el) el.textContent = code;
                }
              }}
            >
              Enter code manually
            </Button>
          </Box>
        </GlassCard>
      </Box>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.xl, m: 2 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {confirmDialog.data?.action === 'check-in'
              ? <Login color="success" />
              : <ExitToApp color="warning" />}
            <Typography variant="h6" fontWeight={700}>
              Confirm {confirmDialog.data?.action === 'check-in' ? 'Check In' : 'Check Out'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={confirmDialog.data?.action === 'check-in' ? 'success' : 'warning'}
            sx={{ borderRadius: RADIUS.md, mb: 2 }}
          >
            {confirmDialog.data?.action === 'check-in' ? 'Checking in to:' : 'Checking out from:'}
          </Alert>
          <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
            {confirmDialog.data?.libraryName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDialog.data?.action === 'check-in'
              ? 'This will start a new library session.'
              : 'This will end your current library session.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCancelAction} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAction}
            disabled={processing}
            sx={{ textTransform: 'none', borderRadius: RADIUS.md }}
          >
            {confirmDialog.data?.action === 'check-in' ? 'Check In' : 'Check Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating dialog — same integration point as original */}
      <RatingDialog
        open={ratingDialog.open}
        onClose={() => setRatingDialog({ open: false, bookingId: null, libraryName: '' })}
        bookingId={ratingDialog.bookingId}
        libraryName={ratingDialog.libraryName}
        onSuccess={() => {}}
      />
    </Box>
  );
}

export default StudentScanner;
